/**
 * Licensed Materials - Property of IBM
 *
 * "Restricted Materials of IBM"
 *
 * (C) Copyright IBM Corp. 2018, 2022
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
const BaseClass = require('gemini/lib/@waca/core-client/js/core-client/ui/core/Class');
const _ = require('underscore');
const UniqueId = require('gemini/lib/@waca/core-client/js/core-client/utils/UniqueId');

import StringResources from 'nls/StringResources';

var ActionHandler = BaseClass.extend({
	/**
	 * Determine if we can execute the action based on the selection type and permissions
	 * For performance reason, we delay the capability check until user selects a source (Content-Nav calls this function for all objects)
	 */
	canExecute: function(options) {
		return this._isSelectionValid(options);
	},

	/**
	 * Determine when we show/hide the option "create explore"  on the menu item
	 */
	isItemVisible: function(options) {
		return this._isSelectionValid(options) && this._hasCapability(options);
	},

	_hasCapability(options) {
		const selections = options.target && options.target.activeObject && options.target.activeObject.aSelectedContext;
		if (selections) {
			for (var i = 0; i < selections.length; i++) {
				const selectedSource = selections[i];
				if (!(selectedSource.effectiveUserCapabilities && selectedSource.effectiveUserCapabilities.length && selectedSource.effectiveUserCapabilities.includes('canUseExploration'))){
					return false;
				}
			}
		}
		return true;
	},

	/**
	 * Default action handler
	 */
	doAction: function(context) {
		// Make a 'created' call to the instrumentation service when we create a new explore
		this._instrumentationTrack(context);
		return this._openCreateView(context);
	},

	_instrumentationTrack: function(context) {
		const instrumentation = context.glassContext.getCoreSvc('.Instrumentation');
		if (instrumentation.enabled) {
			const objectType = 'explore';
			const action = 'created';
			const basePayload = {
				type: 'Created Object',
				objectType,
				action,
				milestoneName: `${action}_${objectType}`
			};
			let dataSourcesInfo = {};
			// If new explore is created from an existing datasource
			if (context.options) {
				dataSourcesInfo = this._getDatasourcesInfo(context.options);
			}
			instrumentation.track({ ...basePayload, ...dataSourcesInfo });
		}
	},

	_getDatasourcesInfo: function(dataSources) {
		const dataSourceInfoMap = {
			uploadedFile: 'custom.counts.uploadedFile',
			module: 'custom.counts.Module',
			package: 'custom.counts.fmPackage'
		};
		const dataSourcesInfo = {};
		dataSources.forEach(({ type }) => {
			if (!dataSourcesInfo[dataSourceInfoMap[type]]) {
				dataSourcesInfo[dataSourceInfoMap[type]] = 0;
			}
			dataSourcesInfo[dataSourceInfoMap[type]] = dataSourcesInfo[dataSourceInfoMap[type]] + 1;
		});
		return dataSourcesInfo;
	},

	/**
	 * Menu item handler
	 */
	onSelectItem: function(context) {
		this.doAction(context);
	},

	// Private
	_getRestrictTemplate: function(/*context*/) {
		return 'explore';
	},

	// Private
	_openCreateView: function(context) {
		const template = this._getRestrictTemplate();
		const sPerspectiveID = UniqueId.get('explore_');
		const perspectiveOptions = {
			id: sPerspectiveID,
			content: {
				id: sPerspectiveID,
				restrictTemplate: template,
				boardSpec: null,
				isAuthoringMode: true
			}
		};
		const sources = this._getSources(context);
		if (sources && sources.length > 0) {
			perspectiveOptions.content.sources = sources;
			perspectiveOptions.content.boardSpec = this._createEmptyBoardSpec();
			return context.glassContext.appController.openAppView('explore', perspectiveOptions).then((view) => {
				return !view.currentContentView.isFeatureEnabled('newStartingPoints', 'false') ? view.currentContentView.openCardListPane() : view.currentContentView.openDatasetpane();
			});
		}

		this._openDataSourceDialog(context, perspectiveOptions);
	},

	// Private function that opens the data source dialog
	_openDataSourceDialog: function(context, perspectiveOptions) {
		const supportedTypes = ['module', 'dataSet2', 'uploadedFile', 'package'];
		const options = {
			'glassContext': context.glassContext,
			'typesToOpen': supportedTypes,
			'capabilitiesRequired': ['canUseExploration'],
			'multiSelect': false,
			'dialogTitle': StringResources.get('addDatasourceToExploreDialogTitle'),
			'primaryBtnText': StringResources.get('addDataSourceDialogButtonLabel'),
			'onOpenCallback': this._onAddDataSource.bind(this, context, perspectiveOptions)
		};
		return context.glassContext.getSvc('.ContentDialogFactory').then(dialogFactory => {
			dialogFactory.createOpenDialog(options).open();
		});
	},

	// Private function which launches explore when data source has been selected from the data source dialog
	_onAddDataSource: function(context, perspectiveOptions, selectedItems) {
		// There is only 1 selected data source in this function because multiSelect is set to false in onAddBtnClick
		const selectedItem = selectedItems[0];
		const dataSource = {
			'assetId': selectedItem.id,
			'name': selectedItem.defaultName,
			'type': selectedItem.type,
			'searchPath': selectedItem.searchPath,
			'isOlapPackage': selectedItem.userInterfaces && selectedItem.userInterfaces.indexOf('analysisStudio') >= 0,
			'modificationTime': selectedItem.modificationTime
		};
		perspectiveOptions.content.sources = [ dataSource ];
		perspectiveOptions.content.boardSpec = this._createEmptyBoardSpec();
		return context.glassContext.appController.openAppView('explore', perspectiveOptions).then((view) => {
			return !view.currentContentView.isFeatureEnabled('newStartingPoints', 'false') ? view.currentContentView.openCardListPane() : view.currentContentView.openDatasetpane();
		});
	},

	//TODO:This function should be a common function for both dashboard and explore 'new' action handlers
	_getSources: function(options) {
		const isSelectionValid = this._isSelectionValid(options);
		var dataSourcesArray = null;
		if (isSelectionValid) {
			dataSourcesArray = [];
			if(!options.target.activeObject){
				return dataSourcesArray;
			}
			var selections = options.target.activeObject.aSelectedContext;
			for(const oSelection of selections){
				dataSourcesArray.push({
					assetId: oSelection.id,
					type: oSelection.type,
					name: oSelection.defaultName,
					searchPath: oSelection.searchPath,
					isOlapPackage: oSelection.userInterfaces && _.indexOf(oSelection.userInterfaces, 'analysisStudio') >= 0
				});
			}
		}
		return dataSourcesArray;
	},

	//TODO:This function should be a common function for both dashboard and explore 'new' action handlers
	_isSelectionValid: function(options) {
		const selection = options.target && options.target.activeObject && options.target.activeObject.aSelectedContext;
		const supportedTypes = ['module', 'dataSet2', 'uploadedFile', 'package', 'data_asset'];
		if (selection && Array.isArray(selection)) {
			for (const item of selection) {
				if (!supportedTypes.includes(item.type)) {
					return false;
				}
				const requiredPermission = 'read';
				if (!(item.permissions && item.permissions.length && item.permissions.includes(requiredPermission))) {
					return false;
				}

				if (item.disabled && item.disabled.toString().toLowerCase() === 'true') {
					return false;
				}
			}
		}
		return true;
	},

	_createEmptyBoardSpec: function() {
		const spec = {
			name: StringResources.get('defaultName'),
			layout: {
				type: 'exploreContainer',
				items: []
			},
			widgets: {
			},
			_meta: {
				bundleID: null
			}
		};
		return spec;
	}
});

export default ActionHandler;



// WEBPACK FOOTER //
// ./src/js/glass/controllers/NewActionHandler.js